import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PreviewRenderer from '../components/PreviewRenderer';
import LanguageSelector, { getPreferredLanguage, SUPPORTED_LANGUAGES } from '../components/LanguageSelector';
import CookieBanner from '../components/CookieBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session ID for analytics
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Track page view with deduplication
const trackedPages = new Set();
const trackPageView = async (subdomain, tenantId, data) => {
  try {
    const pageKey = `${tenantId}:${data.page_id || data.article_id || data.page_slug || 'home'}`;
    
    // Skip if recently tracked
    if (trackedPages.has(pageKey)) return;
    trackedPages.add(pageKey);
    setTimeout(() => trackedPages.delete(pageKey), 5000);
    
    await fetch(`${API_URL}/api/public/track/pageview?tenant_id=${tenantId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Subdomain': subdomain
      },
      body: JSON.stringify({
        ...data,
        session_id: getSessionId(),
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        screen_width: window.screen?.width,
        screen_height: window.screen?.height,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    });
  } catch (e) {
    // Silent fail - don't break the page
  }
};

/**
 * PublicTenantPage - Renders a specific page from a tenant's site
 * Used when accessing pages via subdomain routing (e.g., tenant.nexuscms.ai/about-us)
 * Also used for homepage when a "home" page exists in Page Builder
 * 
 * This component delegates all rendering to PreviewRenderer which handles:
 * - Navigation header with logo and menu items
 * - Page blocks/content
 * - Footer with links and branding
 */
const PublicTenantPage = ({ pageData, subdomain }) => {
  console.log('[PublicTenantPage] Received pageData:', pageData);
  console.log('[PublicTenantPage] isArticle flag:', pageData?.isArticle);
  console.log('[PublicTenantPage] article data:', pageData?.article);
  
  const { page, article, isArticle, tenant_id, tenant_name, theme, branding, menus, all_pages, reseller_branding } = pageData;

  // Apply the tenant's favicon directly to the DOM.
  // Using useEffect (not just Helmet) because react-helmet-async ADDS a new
  // <link rel="icon"> without removing the static one from index.html,
  // leaving two favicon tags. Browsers then use the first one (the platform
  // default). Direct DOM mutation overwrites it reliably for all tag types.
  useEffect(() => {
    const favUrl = branding?.favicon_url;
    if (!favUrl) return;
    ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = favUrl;
    });
    return () => {
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const link = document.querySelector(`link[rel="${rel}"]`);
        if (link) link.href = `${process.env.PUBLIC_URL || ''}/favicon.png`;
      });
    };
  }, [branding?.favicon_url]);
  
  // Track page view on mount
  useEffect(() => {
    if (tenant_id && branding?.enable_builtin_analytics !== false) {
      trackPageView(subdomain, tenant_id, {
        page_id: page?.page_id || null,
        page_slug: page?.slug || null,
        article_id: article?.article_id || null,
        page_type: isArticle ? 'article' : (page?.slug === 'home' ? 'landing' : 'page')
      });
    }
  }, [tenant_id, page?.page_id, article?.article_id, subdomain, isArticle, page?.slug, branding?.enable_builtin_analytics]);
  
  // Translation state
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translatedBlocks, setTranslatedBlocks] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Check if translation is enabled for this tenant
  const translationEnabled = branding?.translation_enabled === true;
  const enabledLanguages = branding?.enabled_languages || null; // null = all languages
  const defaultLanguage = branding?.default_language || 'en';
  const translationMode = branding?.translation_mode || 'selector'; // 'selector' or 'auto'
  
  // Initialize language on mount
  useEffect(() => {
    if (translationEnabled) {
      // First, check for ?lang=XX URL parameter (highest priority - from newsletter links)
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');
      
      if (urlLang && SUPPORTED_LANGUAGES[urlLang]) {
        // URL parameter takes highest priority (newsletter click-through)
        setCurrentLanguage(urlLang);
        // Also save it to localStorage so it persists during navigation
        localStorage.setItem('nexus_language', urlLang);
        return;
      }
      
      // Check if user has manually selected a language (stored in localStorage).
      // Read both the page-level key and the LanguageSelector's own key so a
      // manual selection always survives navigation to another page.
      const savedLang = localStorage.getItem('nexus_language')
        || localStorage.getItem('nexuscms_language_preference');
      
      if (savedLang && SUPPORTED_LANGUAGES[savedLang]) {
        // User has explicitly chosen a language - always respect that
        setCurrentLanguage(savedLang);
      } else if (translationMode === 'auto') {
        // Auto mode: detect from browser language
        const preferred = getPreferredLanguage();
        if (preferred !== defaultLanguage && SUPPORTED_LANGUAGES[preferred]) {
          setCurrentLanguage(preferred);
        } else {
          setCurrentLanguage(defaultLanguage);
        }
      } else {
        // Selector mode: use default language until user manually selects
        setCurrentLanguage(defaultLanguage);
      }
    }
  }, [translationEnabled, defaultLanguage, translationMode]);
  
  // Fetch translation when language changes
  useEffect(() => {
    const fetchTranslation = async () => {
      // Skip if translation not enabled or using default language
      if (!translationEnabled || currentLanguage === defaultLanguage) {
        setTranslatedBlocks(null);
        return;
      }
      
      // Determine what to translate - page blocks or article blocks
      const blocksToTranslate = page?.blocks || article?.blocks;
      const contentId = page?.page_id || article?.article_id;
      
      if (!blocksToTranslate || !contentId) {
        setTranslatedBlocks(null);
        return;
      }
      
      setIsTranslating(true);
      try {
        // Use the public translate-blocks endpoint for both pages and articles
        const response = await fetch(`${API_URL}/api/translation/translate-blocks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            blocks: blocksToTranslate,
            target_language: currentLanguage,
            tenant_id: tenant_id
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.translated_blocks) {
            setTranslatedBlocks(data.translated_blocks);
            console.log(`[Translation] Content translated to ${currentLanguage}`);
          }
        } else {
          console.warn('[Translation] Failed to fetch translation');
          setTranslatedBlocks(null);
        }
      } catch (error) {
        console.error('[Translation] Error:', error);
        setTranslatedBlocks(null);
      } finally {
        setIsTranslating(false);
      }
    };
    
    fetchTranslation();
  }, [currentLanguage, page?.page_id, page?.blocks, article?.article_id, article?.blocks, subdomain, translationEnabled, defaultLanguage, tenant_id]);
  
  const handleLanguageChange = (newLang) => {
    setCurrentLanguage(newLang);
    // Persist so the selection survives navigation to other pages.
    try {
      localStorage.setItem('nexus_language', newLang);
      localStorage.setItem('nexuscms_language_preference', newLang);
    } catch (e) { /* ignore storage errors */ }
  };
  
  // Get blocks to render (translated or original)
  const blocksToRender = translatedBlocks || page?.blocks || [];
  
  // Control DynatexSaaS badge visibility based on branding settings
  // Badge is hidden by default (index.html). We only reveal it once we've
  // confirmed branding allows it — this prevents the "flash on load" issue
  // for white-label tenants.
  useEffect(() => {
    const badge = document.getElementById('dynatex-badge');

    // If tenant belongs to a reseller, rebrand the badge BEFORE revealing it
    if (badge && reseller_branding) {
      badge.href = reseller_branding.website_url || '#';
      const text = badge.querySelector('span');
      if (text) {
        text.textContent = `Made by ${reseller_branding.company_name}`;
        text.style.color = reseller_branding.primary_color || '#4F46E5';
      }
    }

    // Only reveal the badge if branding settings explicitly allow it
    if (branding && branding.show_platform_branding !== false) {
      document.body.classList.add('show-dynatex-badge');
    } else {
      document.body.classList.remove('show-dynatex-badge');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('show-dynatex-badge');
      // Reset badge to default
      const badge = document.getElementById('dynatex-badge');
      if (badge) {
        badge.href = 'https://www.dynatexsaas.com/';
        const text = badge.querySelector('span');
        if (text) {
          text.textContent = 'Made by DynatexSaaS';
          text.style.color = '#374151';
        }
      }
    };
  }, [branding, reseller_branding]);
  
  // Handle anchor link scrolling on page load and hash changes
  useEffect(() => {
    const scrollToAnchor = () => {
      const hash = window.location.hash;
      if (hash) {
        // Remove the # and decode the hash
        const anchorId = decodeURIComponent(hash.substring(1));
        
        // Small delay to ensure the page has rendered
        setTimeout(() => {
          const element = document.getElementById(anchorId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Scroll on initial load
    scrollToAnchor();

    // Listen for hash changes (for in-page anchor navigation)
    window.addEventListener('hashchange', scrollToAnchor);
    
    return () => {
      window.removeEventListener('hashchange', scrollToAnchor);
    };
  }, [page, article]); // Re-run when page or article changes
  
  // Get navigation menus.
  //
  // A menu is visible on the current page when it's marked `is_global` OR
  // its `page_ids` array explicitly includes this page's id. Previously
  // this file picked "the first menu at this location" unconditionally,
  // which meant unchecking a page in the Navigation Manager had no
  // effect — the menu still appeared on every page. Bug reported Aug 2026.
  //
  // If multiple menus qualify (e.g. one global + one page-specific), the
  // page-specific one wins because it's the more explicit intent.
  const currentPageId = pageData.page?.page_id || pageData.article?.article_id || null;
  const pickMenu = (location) => {
    const candidates = (menus || []).filter(m => m.location === location);
    const pageSpecific = candidates.find(m => currentPageId && m.page_ids?.includes(currentPageId));
    if (pageSpecific) return pageSpecific;
    return candidates.find(m => m.is_global) || null;
  };
  const headerMenu = pickMenu('header');
  const footerMenu = pickMenu('footer');
  
  // Handle article rendering
  if (isArticle && pageData.article) {
    const art = pageData.article;
    const navMode = art.article_nav_mode || (art.show_site_navigation === false ? 'none' : 'site_nav');
    console.log('[PublicTenantPage] Rendering ARTICLE, nav mode:', navMode);
    const articleTitle = art.meta_title || art.title || 'Article';
    const fullTitle = `${articleTitle} | ${tenant_name}`;
    const metaDesc = art.meta_description || art.excerpt || '';
    const ogImg = art.og_image || art.image_url || branding?.logo_url || '';
    const canonicalUrl = typeof window !== 'undefined' ? window.location.href : '';
    
    // Build the article body JSX (reused whether nav is on or off)
    const articleBody = (
      <article className="max-w-4xl mx-auto px-4 py-8 md:py-12 bg-white rounded-lg my-8 shadow-sm">
        {/* Featured Image */}
        {art.image_url && (
          <figure className="mb-8">
            <img 
              src={art.image_url} 
              alt={art.image_alt || art.title} 
              className="w-full h-64 md:h-96 object-cover rounded-lg"
            />
            {art.image_caption && (
              <figcaption className="text-xs text-slate-500 mt-2 text-center italic">
                {art.image_caption}
              </figcaption>
            )}
          </figure>
        )}
        
        {/* Article Header */}
        <header className="mb-8">
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{ 
              color: theme?.heading_color || '#1e293b',
              fontFamily: theme?.heading_font || 'inherit'
            }}
          >
            {art.title}
          </h1>
          
          {(art.show_date && art.date) || art.category ? (
            <div className="flex flex-wrap items-center gap-3 text-sm" style={{ color: theme?.body_color || '#64748b' }}>
              {art.show_date && art.date && (
                <time dateTime={art.date}>
                  {new Date(art.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </time>
              )}
              {art.category && (
                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                  backgroundColor: theme?.accent_color ? `${theme.accent_color}20` : '#e0e7ff',
                  color: theme?.accent_color || '#4f46e5'
                }}>
                  {art.category}
                </span>
              )}
            </div>
          ) : null}
        </header>
        
        {/* Article Body - Block Editor Content */}
        {(blocksToRender && blocksToRender.length > 0) || (art.blocks && art.blocks.length > 0) ? (
          <div 
            className="prose prose-lg max-w-none"
            style={{ 
              color: theme?.body_color || '#475569',
              fontFamily: theme?.body_font || 'inherit'
            }}
          >
            {isTranslating && (
              <div className="flex items-center gap-2 text-slate-500 mb-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                <span>Translating...</span>
              </div>
            )}
            <PreviewRenderer 
              blocks={translatedBlocks || art.blocks}
              siteTheme={theme || {}}
              showNavigation={false}
              isQuickPreview={false}
              tenantId={tenant_id}
              subdomain={subdomain}
              currentLanguage={currentLanguage}
              defaultLanguage={defaultLanguage}
              enabledLanguages={enabledLanguages}
              pages={all_pages || []}
            />
          </div>
        ) : art.content_html ? (
          <div 
            className="prose prose-lg max-w-none"
            style={{ 
              color: theme?.body_color || '#475569',
              fontFamily: theme?.body_font || 'inherit'
            }}
            dangerouslySetInnerHTML={{ __html: art.content_html }}
          />
        ) : (
          <p className="text-slate-400 italic">No content</p>
        )}
      </article>
    );
    
    return (
      <>
        <Helmet>
          <title>{fullTitle}</title>
          {metaDesc && <meta name="description" content={metaDesc} />}
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow" />
          
          <meta property="og:type" content="article" />
          <meta property="og:title" content={articleTitle} />
          {metaDesc && <meta property="og:description" content={metaDesc} />}
          {ogImg && <meta property="og:image" content={ogImg} />}
          <meta property="og:url" content={canonicalUrl} />
          {tenant_name && <meta property="og:site_name" content={tenant_name} />}
          {art.date && <meta property="article:published_time" content={art.date} />}
          
          <meta name="twitter:card" content={ogImg ? "summary_large_image" : "summary"} />
          <meta name="twitter:title" content={articleTitle} />
          {metaDesc && <meta name="twitter:description" content={metaDesc} />}
          {ogImg && <meta name="twitter:image" content={ogImg} />}
        </Helmet>
        
        {navMode === 'site_nav' ? (
          /* Full site navigation wrapper */
          <PreviewRenderer 
            blocks={[]}
            siteTheme={theme || {}}
            headerMenu={headerMenu}
            footerMenu={footerMenu}
            showNavigation={true}
            isQuickPreview={false}
            tenantId={tenant_id}
            pages={all_pages || []}
            subdomain={subdomain}
            currentLanguage={currentLanguage}
            defaultLanguage={defaultLanguage}
          >
            {articleBody}
          </PreviewRenderer>
        ) : (
          <div className="min-h-screen flex flex-col" style={{ backgroundColor: theme?.backgroundColor || '#ffffff' }}>
            {navMode === 'back_link' && (
              <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 py-3">
                  <a 
                    href="/"
                    className="inline-flex items-center text-sm font-medium hover:underline"
                    style={{ color: theme?.accent_color || '#4f46e5' }}
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    Back to {tenant_name || 'Site'}
                  </a>
                </div>
              </div>
            )}
            <main className="flex-grow">
              {articleBody}
            </main>
          </div>
        )}
        <CookieBanner settings={branding?.cookie_consent} />
      </>
    );
  }
  
  // Build page title (for regular pages)
  const pageTitle = page?.title && page?.slug !== 'home' && !page?.isHomepage 
    ? `${page.title} | ${tenant_name}` 
    : tenant_name || 'Page';
  
  // SEO meta values with fallbacks
  const metaDescription = page?.meta_description || '';
  const ogTitle = page?.og_title || pageTitle;
  const ogDescription = page?.og_description || metaDescription;
  const ogImage = page?.og_image || branding?.logo_url || '';
  const canonicalUrl = page?.canonical_url || (typeof window !== 'undefined' ? window.location.href : '');
  const noindex = page?.noindex || false;
  
  if (!page) {
    console.log('[PublicTenantPage] No page found, showing 404. isArticle was:', isArticle, 'article was:', article);
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Helmet>
          <title>Page Not Found | {tenant_name || 'Site'}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Page Not Found</h1>
          <p className="text-slate-600">This page does not exist or has not been published.</p>
        </div>
      </div>
    );
  }

  // Use page-specific theme or fall back to tenant theme
  const pageTheme = page.theme || theme || {};

  console.log('[PublicTenantPage] Rendering PAGE with navigation:', { headerMenu: !!headerMenu, footerMenu: !!footerMenu });

  // Let PreviewRenderer handle everything including navigation
  // Don't pass title - pages with blocks handle their own layout
  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{pageTitle}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        
        {/* Robots */}
        {noindex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={ogTitle} />
        {ogDescription && <meta property="og:description" content={ogDescription} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {tenant_name && <meta property="og:site_name" content={tenant_name} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={ogTitle} />
        {ogDescription && <meta name="twitter:description" content={ogDescription} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        
        {/* Language */}
        <html lang={currentLanguage} />

        {/* Tenant favicon is applied via useEffect above (direct DOM mutation).
            This avoids react-helmet-async leaving a duplicate <link rel="icon">
            alongside the static one in index.html. */}

        {/* Mobile browser chrome tint (Safari iOS / Chrome Android address bar).
            Uses the tenant's primary brand colour so the site feels fully their own. */}
        {pageTheme?.primary_color && (
          <meta name="theme-color" content={pageTheme.primary_color} />
        )}

        {/* PWA manifest — branded "Add to Home Screen" experience.
            The endpoint identifies the tenant from the current hostname. */}
        {typeof window !== 'undefined' && (
          <link
            rel="manifest"
            href={`/api/public/manifest.webmanifest?domain=${encodeURIComponent(window.location.hostname)}`}
          />
        )}
      </Helmet>
      
      {/* Language Selector — position controlled by Site Theme:
            - "floating" (default): bottom-left on mobile, top-right on desktop
            - "top_bar": slim strip anchored at the very top of the page
            - "hidden": don't render at all (tenant places it themselves) */}
      {translationEnabled && (theme?.language_selector_position || 'floating') !== 'hidden' && (
        (theme?.language_selector_position || 'floating') === 'top_bar' ? (
          <div className="sticky top-0 z-40 w-full bg-slate-50 border-b border-slate-200 flex justify-end items-center px-4 py-1.5">
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              enabledLanguages={enabledLanguages}
              variant="compact"
              className={isTranslating ? 'opacity-50 pointer-events-none' : ''}
            />
          </div>
        ) : (
          <div className="fixed bottom-4 left-4 md:top-4 md:right-4 md:bottom-auto md:left-auto z-50">
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              enabledLanguages={enabledLanguages}
              variant="compact"
              className={isTranslating ? 'opacity-50 pointer-events-none' : ''}
            />
          </div>
        )
      )}
      
      {/* Translation Loading Indicator — sits just above the language
          selector on mobile (bottom-left) and just below it on desktop
          (top-right) so the two never collide. */}
      {isTranslating && (
        <div className="fixed bottom-16 left-4 md:top-16 md:right-4 md:bottom-auto md:left-auto z-50 bg-white shadow-lg rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-slate-600">
          <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Translating...
        </div>
      )}
      
      <PreviewRenderer 
        blocks={blocksToRender}
        title={null}
        pageWidth={page.pageWidth || 'standard'}
        theme={pageTheme}
        siteTheme={theme || pageTheme || {}}
        headerMenu={headerMenu}
        footerMenu={footerMenu}
        showNavigation={true}
        isQuickPreview={false}
        tenantId={tenant_id}
        pages={all_pages || []}
        subdomain={subdomain}
        currentLanguage={currentLanguage}
        defaultLanguage={defaultLanguage}
        enabledLanguages={enabledLanguages}
      />
      <CookieBanner settings={branding?.cookie_consent} />
    </>
  );
};

export default PublicTenantPage;
